import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './AdminChat.css';

const AdminChat = ({ socket }) => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [roomSearch, setRoomSearch] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [showAlbum, setShowAlbum] = useState(false);
  const [albumImages, setAlbumImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [liveNotice, setLiveNotice] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedRoomRef = useRef(null);
  const seenMessageIdsRef = useRef(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Enhanced debug logging
  useEffect(() => {
    console.log('🔍 AdminChat - User object:', user);
    console.log('🔍 AdminChat - User role:', user?.role);
    console.log('🔍 AdminChat - Is admin/moderator:', user && ['admin', 'moderator'].includes(user.role));
    
    if (user) {
        const debugText = `User: ${user.email}, Role: ${user.role}, ID: ${user.id}, Live: ${socket?.connected ? 'on' : 'off'}`;
      setDebugInfo(debugText);
      console.log('🔍 ' + debugText);
    } else {
      console.log('🔍 No user object found');
      setError('No user information available. Please log in again.');
    }
  }, [user]);

  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);

  useEffect(() => {
    if (user && ['admin', 'moderator'].includes(user.role)) {
      console.log('✅ User has admin privileges, loading rooms...');
      setError('');
      loadRooms();
    } else if (user) {
      console.log('❌ User does not have admin privileges');
      setError(`Access denied. Your role: "${user.role}". Required: admin or moderator.`);
    }
  }, [user]);

  useEffect(() => {
    if (!socket || !user || !['admin', 'moderator'].includes(user.role)) return;

    const joinAdmin = () => {
      socket.emit('joinAdminRoom');
    };

    joinAdmin();
    socket.on('connect', joinAdmin);
    socket.on('authenticated', joinAdmin);

    const handleIncoming = (message) => {
      if (!message || !message.id) return;
      if (seenMessageIdsRef.current.has(message.id)) return;
      seenMessageIdsRef.current.add(message.id);

      const currentRoom = selectedRoomRef.current;
      const isCurrentRoom = currentRoom && Number(message.room_id) === Number(currentRoom.id);
      const isFromUser = message.sender_role === 'user';

      if (isCurrentRoom) {
        setMessages((prev) => (prev.some((item) => item.id === message.id) ? prev : [...prev, message]));
      }

      setRooms((prev) => {
        const roomId = Number(message.room_id);
        const existing = prev.find((room) => Number(room.id) === roomId);
        const unreadBump = isFromUser && !isCurrentRoom ? 1 : 0;
        const updatedRoom = {
          ...(existing || message.room || { id: roomId }),
          last_message_at: message.created_at || new Date().toISOString(),
          unread_count: Number(existing?.unread_count || 0) + unreadBump
        };
        const others = prev.filter((room) => Number(room.id) !== roomId);
        return [updatedRoom, ...others];
      });

      if (isFromUser) {
        const preview = message.message_type === 'image'
          ? 'Sent an image'
          : message.message_type === 'file'
            ? 'Sent a file'
            : (message.message || 'New message');
        const title = message.sender_name || message.room?.user_name || 'Customer';
        setLiveNotice(`${title}: ${preview}`);
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification(`Support chat · ${title}`, { body: preview });
          } catch (err) {
            console.warn('Browser notification failed:', err);
          }
        }
      }
    };

    socket.on('newMessage', handleIncoming);
    socket.on('adminChatMessage', handleIncoming);

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    return () => {
      socket.off('connect', joinAdmin);
      socket.off('authenticated', joinAdmin);
      socket.off('newMessage', handleIncoming);
      socket.off('adminChatMessage', handleIncoming);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!liveNotice) return undefined;
    const timer = setTimeout(() => setLiveNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [liveNotice]);

  useEffect(() => {
    if (!socket || !selectedRoom) return;
    socket.emit('joinChatRoom', selectedRoom.id);
    return () => {
      socket.emit('leaveChatRoom', selectedRoom.id);
    };
  }, [socket, selectedRoom]);

  // Auto-focus input when room is selected or messages update
  useEffect(() => {
    if (selectedRoom && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedRoom, messages]);

  const loadRooms = async () => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found. Please login again.');
        return;
      }

      console.log('📡 Loading admin rooms...');
      
      const response = await fetch('https://trcsite.net/api/chat/admin/rooms', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Admin rooms response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Admin loaded rooms:', data.length);
        setRooms(data);
        setError('');
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to load rooms:', errorData);
        setError(errorData.error || `Failed to load rooms. Status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error loading rooms:', error);
      setError('Failed to load rooms: ' + error.message);
    }
  };

  const selectRoom = async (room) => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`https://trcsite.net/api/chat/admin/room/${room.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Admin selected room:', data.room);
        setSelectedRoom(data.room);
        setMessages(data.messages);
        data.messages.forEach((msg) => {
          if (msg?.id) seenMessageIdsRef.current.add(msg.id);
        });
        markMessagesAsRead(data.room.id);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load room');
      }
    } catch (error) {
      console.error('❌ Error loading room:', error);
      setError('Failed to load room: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAlbum = async () => {
    if (!selectedRoom) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`https://trcsite.net/api/chat/room/${selectedRoom.id}/album`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAlbumImages(data.images);
        setShowAlbum(true);
      } else {
        const errorData = await response.json();
        console.error('Failed to load album:', errorData);
        setError('Failed to load photo album');
      }
    } catch (error) {
      console.error('Error loading album:', error);
      setError('Failed to load photo album');
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (roomId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch('https://trcsite.net/api/chat/messages/read', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ room_id: roomId })
      });
      loadRooms(); // Refresh unread counts
    } catch (error) {
      console.error('❌ Error marking messages as read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom || sending) return;

    try {
      setSending(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch('https://trcsite.net/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          room_id: selectedRoom.id,
          message: newMessage.trim()
        })
      });

      if (response.ok) {
        const sentMessage = await response.json();
        console.log('✅ Admin sent message:', sentMessage);
        if (sentMessage?.id) {
          seenMessageIdsRef.current.add(sentMessage.id);
          setMessages((prev) => (prev.some((item) => item.id === sentMessage.id) ? prev : [...prev, sentMessage]));
        }
        setNewMessage('');
        // Auto-focus after sending
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setError('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('File selected:', file.name, file.type, file.size);

    // Check if file is an image
    if (file.type.startsWith('image/')) {
      // Show preview for images
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage({
          file: file,
          preview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      // Upload non-image files directly
      uploadFile(file);
    }
  };

  const uploadFile = async (file, caption = '') => {
    if (!file || !selectedRoom) return;

    try {
      setUploading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('room_id', selectedRoom.id);
      formData.append('message', caption || `Shared ${file.type.startsWith('image/') ? 'image' : 'file'}: ${file.name}`);

      console.log('Uploading file:', file.name, 'to room:', selectedRoom.id);

      const response = await fetch('https://trcsite.net/api/chat/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const sentMessage = await response.json();
        console.log('✅ File uploaded successfully:', sentMessage);
        setPreviewImage(null);
        // Auto-focus after upload
        if (inputRef.current) {
          inputRef.current.focus();
        }
      } else {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        setError(errorData.error || 'Failed to upload file');
      }
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      setError('Failed to upload file: ' + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const sendImageWithCaption = async (caption) => {
    if (!previewImage) return;
    await uploadFile(previewImage.file, caption);
  };

  const cancelPreview = () => {
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateRoomStatus = async (status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://trcsite.net/api/chat/admin/room/${selectedRoom.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        setSelectedRoom(prev => ({ ...prev, status }));
        loadRooms();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update room status');
      }
    } catch (error) {
      console.error('❌ Error updating room status:', error);
      setError('Failed to update room status: ' + error.message);
    }
  };

  const refreshRooms = () => {
    loadRooms();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const hasImages = messages.some(msg => msg.message_type === 'image' && msg.file_url);
  const normalizedSearch = roomSearch.trim().toLowerCase();
  const filteredRooms = normalizedSearch
    ? rooms.filter((room) => {
        const fields = [
          room?.user_name,
          room?.user_email,
          room?.room_name,
          room?.status,
          room?.id
        ];
        return fields.some((value) =>
          (value ?? '').toString().toLowerCase().includes(normalizedSearch)
        );
      })
    : rooms;

  // Show loading while checking authentication
  if (!user) {
    return (
      <div className="admin-chat">
        <div className="loading-full">
          <h3>Loading user information...</h3>
          <p>Please wait while we authenticate your session.</p>
        </div>
      </div>
    );
  }

  // Check if user has admin privileges
  const isAdmin = user && ['admin', 'moderator'].includes(user.role);
  
  if (!isAdmin) {
    return (
      <div className="admin-chat">
        <div className="access-denied">
          <h2>🚫 Access Denied</h2>
          <p>You need administrator privileges to access the admin chat.</p>
          <div className="user-info">
            <h4>Your Account Information:</h4>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Role:</strong> {user.role}</p>
            <p><strong>Required Role:</strong> admin or moderator</p>
          </div>
          {debugInfo && (
            <div className="debug-info">
              <p><strong>Debug Info:</strong> {debugInfo}</p>
            </div>
          )}
          <div className="action-buttons">
            <button onClick={() => window.location.href = '/dashboard'} className="btn-primary">
              Go to Dashboard
            </button>
            <button onClick={refreshRooms} className="btn-secondary">
              Retry Access Check
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-chat">
      <div className="admin-header">
        <div className="header-left">
          <h2>💬 Admin Support Chat</h2>
          <span className="user-welcome">Welcome, {user.name} ({user.role})</span>
        </div>
        <div className="header-right">
          <button onClick={refreshRooms} className="btn-refresh" title="Refresh rooms">
            🔄 Refresh
          </button>
          <span className="room-count">{rooms.length} room(s)</span>
        </div>
      </div>

      {liveNotice && (
        <div className="admin-live-notice" role="status">
          <strong>New message</strong>
          <span>{liveNotice}</span>
        </div>
      )}

      {debugInfo && (
        <div className="debug-info-bar">
          <small>Debug: {debugInfo}</small>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="image-preview-modal">
          <div className="image-preview-content">
            <div className="preview-header">
              <h4>Send Image</h4>
              <button onClick={cancelPreview}>×</button>
            </div>
            <div className="preview-actions">
              <input
                type="text"
                placeholder="Add a caption (optional)"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    sendImageWithCaption(e.target.value);
                  }
                }}
                autoFocus
              />
              <div className="preview-buttons">
                <button onClick={cancelPreview} className="btn-cancel">
                  Cancel
                </button>
                <button 
                  onClick={() => sendImageWithCaption('')}
                  className="btn-send"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <div className="sending-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    'Send Image'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Album Modal */}
      {showAlbum && (
        <div className="album-modal">
          <div className="album-modal-content">
            <div className="album-header">
              <h3>📸 Photo Album</h3>
              <button onClick={() => setShowAlbum(false)}>×</button>
            </div>
            <div className="album-grid">
              {loading ? (
                <div className="album-loading">
                  <div className="loading-spinner"></div>
                  <span>Loading album...</span>
                </div>
              ) : albumImages.length === 0 ? (
                <div className="no-images">No images shared yet</div>
              ) : (
                albumImages.map((image) => (
                  <div 
                    key={image.id} 
                    className="album-item"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img 
                      src={`https://trcsite.net${image.file_url}`} 
                      alt={image.file_name}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150x150/667eea/ffffff?text=Image+Not+Found';
                      }}
                    />
                    <div className="album-item-info">
                      <span className="file-name">{image.file_name}</span>
                      <span className="upload-date">
                        {new Date(image.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image View Modal - Clean Version */}
      {selectedImage && (
        <div className="image-modal">
          <div className="image-modal-content">
            <div className="image-modal-header">
              <span>{selectedImage.file_name}</span>
              <button onClick={() => setSelectedImage(null)}>×</button>
            </div>
            <div className="image-modal-body">
              <img 
                src={`https://trcsite.net${selectedImage.file_url}`} 
                alt={selectedImage.file_name}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300/667eea/ffffff?text=Image+Not+Found';
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="chat-container">
        <div className="chat-rooms-sidebar">
          <div className="sidebar-header">
            <h3>Customer Support Rooms</h3>
            {rooms.length > 0 && (
              <span className="unread-total">
                {rooms.reduce((total, room) => total + (room.unread_count || 0), 0)} unread
              </span>
            )}
          </div>
          
          {error && (
            <div className="admin-chat-error">
              {error}
              <button onClick={() => setError('')} className="error-close">×</button>
            </div>
          )}

          <div className="rooms-search">
            <div className="rooms-search-input-wrap">
              <span className="rooms-search-icon">🔎</span>
              <input
                className="rooms-search-input"
                type="text"
                value={roomSearch}
                onChange={(e) => setRoomSearch(e.target.value)}
                placeholder="Search user / email / status..."
              />
              {roomSearch.trim() && (
                <button
                  type="button"
                  className="rooms-search-clear"
                  onClick={() => setRoomSearch('')}
                  title="Clear"
                >
                  ×
                </button>
              )}
            </div>
            <div className="rooms-search-meta">
              <span>{filteredRooms.length} shown</span>
              <span>•</span>
              <span>{rooms.length} total</span>
            </div>
          </div>

          <div className="rooms-list">
            {rooms.length === 0 ? (
              <div className="no-rooms">
                <p>No chat rooms available</p>
                <p className="hint">When users contact support, their rooms will appear here.</p>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="no-rooms">
                <p>No results found</p>
                <p className="hint">Try searching by user name, email, or status.</p>
              </div>
            ) : (
              filteredRooms.map(room => (
                <div
                  key={room.id}
                  className={`room-item ${selectedRoom?.id === room.id ? 'active' : ''}`}
                  onClick={() => selectRoom(room)}
                >
                  <div className="room-user">
                    <strong>{room.user_name}</strong>
                    <span className="user-email">{room.user_email}</span>
                  </div>
                  <div className="room-meta">
                    <span className={`status-badge ${room.status}`}>
                      {room.status}
                    </span>
                    {room.unread_count > 0 && (
                      <span className="unread-badge">{room.unread_count}</span>
                    )}
                  </div>
                  <div className="room-time">
                    {new Date(room.last_message_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="chat-area">
          {selectedRoom ? (
            <>
              <div className="chat-header">
                <div className="room-info">
                  <h4>{selectedRoom.user_name}</h4>
                  <span className="user-email">{selectedRoom.user_email}</span>
                </div>
                <div className="room-actions">
                  {hasImages && (
                    <button 
                      className="album-btn"
                      onClick={loadAlbum}
                      title="View Photo Album"
                      disabled={loading}
                    >
                      📸 Album
                    </button>
                  )}
                  <label>Status: </label>
                  <select 
                    value={selectedRoom.status} 
                    onChange={(e) => updateRoomStatus(e.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>

              <div className="messages-container">
                {loading ? (
                  <div className="loading">
                    <div className="loading-spinner"></div>
                    <span>Loading messages...</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="no-messages">
                    <div className="no-messages-icon">💬</div>
                    <p>No messages in this conversation yet.</p>
                    <p className="hint">Start the conversation by sending a welcome message!</p>
                    <div className="upload-hint">
                      <p>📎 Click the paperclip icon to upload photos and files</p>
                    </div>
                  </div>
                ) : (
                  messages.map(message => (
                    <div
                      key={message.id}
                      className={`message ${message.sender_role === 'admin' || message.sender_role === 'moderator' ? 'admin-message' : 'user-message'}`}
                    >
                      <div className="message-header">
                        <span className="sender-name">
                          {message.sender_name} ({message.sender_role})
                        </span>
                        <span className="message-time">
                          {formatTime(message.created_at)}
                        </span>
                      </div>

                      {message.message_type === 'image' && message.file_url ? (
                        <div className="message-image">
                          <img 
                            src={`https://trcsite.net${message.file_url}`} 
                            alt={message.file_name || 'Shared image'}
                            onClick={() => setSelectedImage(message)}
                            onError={(e) => {
                              console.error('Failed to load image:', message.file_url);
                              e.target.src = 'https://via.placeholder.com/200x150/667eea/ffffff?text=Image+Not+Found';
                            }}
                          />
                          <div className="image-caption">
                            {message.message && message.message !== `Shared image: ${message.file_name}` && (
                              <p>{message.message}</p>
                            )}
                            <span className="file-info">
                              {message.file_name} • {formatFileSize(message.file_size || 0)}
                            </span>
                          </div>
                        </div>
                      ) : message.message_type === 'file' && message.file_url ? (
                        <div className="message-file">
                          <div className="file-icon">📎</div>
                          <div className="file-info">
                            <a 
                              href={`https://trcsite.net${message.file_url}`} 
                              download={message.file_name}
                              className="file-link"
                            >
                              {message.file_name}
                            </a>
                            <span className="file-size">{formatFileSize(message.file_size || 0)}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="message-content">
                          {message.message}
                        </div>
                      )}

                      {message.sender_id === user.id && (
                        <div className="message-status">
                          {message.is_read ? 'Read' : 'Sent'}
                        </div>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={sendMessage} className="message-form">
                <div className="input-container">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your response..."
                    disabled={sending || loading || uploading}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(e);
                      }
                    }}
                    autoFocus
                  />
                  <div className="action-buttons">
                    <label className="file-upload-btn" title="Upload photo or file">
                      📎
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        disabled={uploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending || loading || uploading}
                      className={`send-btn ${sending ? 'sending' : ''}`}
                    >
                      {sending ? (
                        <>
                          <div className="sending-spinner"></div>
                          Sending...
                        </>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                </div>
                
                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <span>Uploading file...</span>
                  </div>
                )}
              </form>
            </>
          ) : (
            <div className="no-room-selected">
              <div className="selection-prompt">
                <h3>👈 Select a Conversation</h3>
                <p>Choose a chat room from the sidebar to start messaging</p>
                <div className="features-list">
                  <div className="feature">
                    <strong>💬 Real-time Chat</strong>
                    <p>Messages update instantly for both users and admins</p>
                  </div>
                  <div className="feature">
                    <strong>🖼️ Photo Sharing</strong>
                    <p>Share images and files with customers</p>
                  </div>
                  <div className="feature">
                    <strong>👁️ Read Status</strong>
                    <p>See when messages have been read by the other party</p>
                  </div>
                  <div className="feature">
                    <strong>📊 Room Management</strong>
                    <p>Track open, closed, and resolved conversations</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChat;
